import React, { PureComponent, useState } from "react";

import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    BarChart,
    Bar,
    ComposedChart,
    Label,
    Brush,
} from "recharts";

import { PlusOutlined } from "@ant-design/icons";

import PropTypes from "prop-types";

const propTypes = {
    variables: PropTypes.arrayOf(
        PropTypes.exact({
            key: PropTypes.string,
            color: PropTypes.string,
            name: PropTypes.string,
            totales: PropTypes.array,
            totalUnit: PropTypes.string,
        })
    ),
    data: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number,
            name: PropTypes.string,
        })
    ),
    chartType: PropTypes.oneOf(["Line", "Bar"]).isRequired,
    brush: PropTypes.bool,
};

const defaultProps = {
    variables: [],
    data: [],
    chartType: "Line",
    brush: true,
};

const CustomChart = (props) => {
    if (props.type == "Bar") {
        return <BarChart {...props}>{props.children}</BarChart>;
    }

    return <LineChart {...props}>{props.children}</LineChart>;
};

const renderLines = (variables, type) => {
    if (type == "Bar") {
        return (
            <>
                {(variables || []).map((item) => (
                    <Bar
                        key={item?.key}
                        connectNulls
                        yAxisId="left"
                        type="monotone"
                        dataKey={item?.key}
                        name={item?.name}
                        fill={item?.color}
                        label={<CustomizedLabel />}
                        label={{ position: "top" }}
                    />
                ))}
            </>
        );
    }

    return (
        <>
            {(variables || []).map((item, index) => (
                <Line
                    key={item?.key}
                    connectNulls
                    yAxisId="left"
                    type="monotone"
                    dataKey={item?.key}
                    name={item?.name}
                    stroke={item?.color}
                    label={<CustomizedLabel />}
                />
            ))}
        </>
    );
};

class CustomizedAxisTick extends PureComponent {
    render() {
        const { x, y, stroke, payload } = this.props;

        return (
            <g transform={`translate(${x},${y})`}>
                <text x={0} y={0} dy={16} textAnchor="end" fill="#666" transform="rotate(-35)">
                    {payload.value}
                </text>
            </g>
        );
    }
}

class CustomizedLabel extends PureComponent {
    render() {
        const { x, y, stroke, value } = this.props;

        return (
            <text x={x} y={y} dy={-4} fill={stroke} fontSize={10} textAnchor="middle">
                {value}
            </text>
        );
    }
}

const CustomTooltip = (props) => {
    const { active, payload, label } = props;
    if (active && payload && payload.length) {
        return (
            <div
                className="recharts-default-tooltip"
                style={{
                    margin: 0,
                    padding: 10,
                    backgroundColor: "rgb(255, 255, 255)",
                    border: "1px solid rgb(204, 204, 204)",
                    whiteSpace: "nowrap",
                }}
            >
                <p className="recharts-tooltip-label" style={{ margin: 0 }}>
                    {label}
                </p>
                <ul className="recharts-tooltip-item-list" style={{ padding: 0, margin: 0 }}>
                    {payload.map((item) => (
                        <li
                            className="recharts-tooltip-item"
                            style={{ display: "block", paddingTop: 4, paddingBottom: 4, color: `${item.color}` }}
                        >
                            <span className="recharts-tooltip-item-name">{item.name}</span>
                            <span className="recharts-tooltip-item-separator"> : </span>
                            <span className="recharts-tooltip-item-value">{item.value}(total)</span>
                            <span className="recharts-tooltip-item-unit"></span>
                        </li>
                    ))}
                </ul>
            </div>
        );
    }

    return null;
};

const ResponsiveChart = (props) => {
    const { chartType, data, variables, brush, result } = props;

    const yAxis = () => {
        if (props.result === "Percent") {
            return { label: "Percent", domain: ["dataMin - 1", 100], unit: "%" };
        }

        if (props.result === "Quantity") {
            return { label: "Quantity", domain: false, unit: "" };
        }

        if (props.result === "Price") {
            return { label: "Price", domain: false, unit: "$" };
        }

        return { label: "Number", domain: [0, "dataMax + 1"], unit: "" };
    };

    const yAxisData = yAxis();

    const formatterTooltip = (value, name, varProps, algo, xx) => {
        const tooltip = props.getTooltip(varProps?.dataKey, varProps?.payload?.id);
        return `${value}${tooltip}`;
    };

    return (
        <div style={{ width: "100%", height: 500 }}>
            <ResponsiveContainer>
                <CustomChart
                    type={chartType}
                    width={500}
                    height={500}
                    data={data}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                    <CartesianGrid strokeDasharray="2 2" />
                    <Legend verticalAlign="top" height={36} />
                    <XAxis dataKey="name">
                        <Label value="Months" position="bottom" />
                    </XAxis>
                    <YAxis
                        yAxisId="left"
                        label={{
                            value: yAxisData.label,
                            angle: -90,
                            position: "left",
                            textAnchor: "middle",
                            ffset: 90,
                        }}
                        domain={yAxisData.domain}
                        unit={yAxisData.unit}
                    />

                    <Tooltip formatter={formatterTooltip} ontent={<CustomTooltip />} />

                    {renderLines(variables, chartType)}
                    {brush && <Brush />}
                </CustomChart>
            </ResponsiveContainer>
        </div>
    );
};

ResponsiveChart.propTypes = propTypes;
ResponsiveChart.defaultProps = defaultProps;

export default ResponsiveChart;
